import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const IndustryResources = () => {
  const industries = [
    {
      id: 'finance',
      name: 'Financial Services',
      icon: 'Banknote',
      description: 'Regulatory compliance, risk management, and customer analytics for banking and fintech.',
      resourceCount: 28,
      image: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=400&h=250&fit=crop',
      color: 'bg-blue-500',
      featured: [
        'Basel III Compliance Framework',
        'Customer 360 Analytics Guide',
        'Fraud Detection Best Practices'
      ]
    },
    {
      id: 'healthcare',
      name: 'Healthcare & Life Sciences',
      icon: 'Heart',
      description: 'HIPAA compliance, clinical data management, and patient outcome analytics.',
      resourceCount: 22,
      image: 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1f?w=400&h=250&fit=crop',
      color: 'bg-green-500',
      featured: [
        'HIPAA Data Governance Template',
        'Clinical Trial Data Management',
        'Patient Privacy Framework'
      ]
    },
    {
      id: 'retail',
      name: 'Retail & E-commerce',
      icon: 'ShoppingCart',
      description: 'Customer journey analytics, inventory optimization, and personalization strategies.',
      resourceCount: 35,
      image: 'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=400&h=250&fit=crop',
      color: 'bg-purple-500',
      featured: [
        'Customer Segmentation Playbook',
        'Inventory Analytics Framework',
        'Personalization Strategy Guide'
      ]
    },
    {
      id: 'manufacturing',
      name: 'Manufacturing',
      icon: 'Factory',
      description: 'IoT data integration, predictive maintenance, and supply chain optimization.',
      resourceCount: 19,
      image: 'https://images.unsplash.com/photo-1565514020179-026b92b84bb6?w=400&h=250&fit=crop',
      color: 'bg-orange-500',
      featured: [
        'IoT Data Architecture Guide',
        'Predictive Maintenance Framework',
        'Supply Chain Analytics'
      ]
    },
    {
      id: 'technology',
      name: 'Technology',
      icon: 'Cpu',
      description: 'Product analytics, user behavior tracking, and performance optimization.',
      resourceCount: 31,
      image: 'https://images.unsplash.com/photo-1518709268805-4e9042af2176?w=400&h=250&fit=crop',
      color: 'bg-teal-500',
      featured: [
        'Product Analytics Framework',
        'User Behavior Tracking Guide',
        'A/B Testing Best Practices'
      ]
    },
    {
      id: 'energy',
      name: 'Energy & Utilities',
      icon: 'Zap',
      description: 'Smart grid analytics, energy consumption optimization, and regulatory reporting.',
      resourceCount: 16,
      image: 'https://images.unsplash.com/photo-1473341304170-971dccb5ac1e?w=400&h=250&fit=crop',
      color: 'bg-yellow-500',
      featured: [
        'Smart Grid Data Management',
        'Energy Consumption Analytics',
        'Utility Regulatory Compliance'
      ]
    }
  ];

  const trendingResources = [
    {
      id: 1,
      title: 'Cross-Industry Data Governance Comparison',
      description: 'Comprehensive analysis of data governance approaches across different industries.',
      downloadCount: '3.2k',
      industry: 'All Industries'
    },
    {
      id: 2,
      title: 'Regulatory Compliance Matrix 2024',
      description: 'Updated compliance requirements across major industries and regions.',
      downloadCount: '2.8k',
      industry: 'All Industries'
    },
    {
      id: 3,
      title: 'Industry Benchmarking Report',
      description: 'Data maturity benchmarks and best practices by industry vertical.',
      downloadCount: '2.1k',
      industry: 'All Industries'
    }
  ];

  return (
    <section className="py-16 lg:py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-section-title text-primary mb-4">Industry-Specific Resources</h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Tailored content and frameworks designed for your industry's unique challenges and requirements
          </p>
        </div>

        {/* Industry Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
          {industries?.map((industry) => (
            <div
              key={industry?.id}
              className="bg-white rounded-xl shadow-subtle hover:shadow-elevation transition-all duration-300 overflow-hidden hover:scale-105 active:scale-95 cursor-pointer"
            >
              <div className="relative">
                <Image
                  src={industry?.image}
                  alt={industry?.name}
                  className="w-full h-48 object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                <div className="absolute bottom-4 left-4 right-4">
                  <div className="flex items-center space-x-3 mb-2">
                    <div className={`p-2 rounded-lg ${industry?.color} text-white`}>
                      <Icon name={industry?.icon} size={20} />
                    </div>
                    <div className="text-white">
                      <h3 className="font-semibold text-lg">{industry?.name}</h3>
                      <p className="text-white/80 text-sm">{industry?.resourceCount} resources</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="p-6">
                <p className="text-muted-foreground mb-4">
                  {industry?.description}
                </p>

                <div className="space-y-2 mb-6">
                  <h4 className="text-sm font-semibold text-primary">Featured Resources:</h4>
                  {industry?.featured?.map((resource, index) => (
                    <div key={index} className="flex items-center space-x-2 text-sm text-muted-foreground">
                      <Icon name="CheckCircle" size={14} className="text-secondary" />
                      <span>{resource}</span>
                    </div>
                  ))}
                </div>

                <Button
                  variant="outline"
                  iconName="ArrowRight"
                  iconPosition="right"
                  className="w-full hover:scale-105 active:scale-95 transition-all duration-200"
                >
                  Explore {industry?.name}
                </Button>
              </div>
            </div>
          ))}
        </div>

        {/* Trending Cross-Industry Resources */}
        <div className="bg-surface rounded-2xl p-8 lg:p-12">
          <div className="text-center mb-8">
            <h3 className="text-2xl font-bold text-primary mb-4">Cross-Industry Insights</h3>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Popular resources that provide value across multiple industries and use cases
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {trendingResources?.map((resource) => (
              <div
                key={resource?.id}
                className="bg-white rounded-xl p-6 shadow-subtle hover:shadow-elevation hover:scale-105 transition-all duration-300"
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1">
                    <h4 className="font-semibold text-primary mb-2">{resource?.title}</h4>
                    <p className="text-sm text-muted-foreground mb-3">{resource?.description}</p>
                    <div className="text-xs text-secondary font-medium">{resource?.industry}</div>
                  </div>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-1 text-sm text-muted-foreground">
                    <Icon name="Download" size={14} />
                    <span>{resource?.downloadCount} downloads</span>
                  </div>
                  <Button variant="ghost" size="sm" iconName="ArrowRight" iconPosition="right" className="hover:scale-110 active:scale-95 transition-all duration-200">
                    Access
                  </Button>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-8">
            <Button
              variant="default"
              iconName="Library"
              iconPosition="left"
              className="bg-secondary hover:bg-secondary/90 hover:scale-105 active:scale-95 transition-all duration-200"
            >
              Browse All Cross-Industry Resources
            </Button>
          </div>
        </div>

        {/* Industry Request CTA */}
        <div className="mt-16 bg-gradient-brand rounded-2xl p-8 lg:p-12 text-center text-white">
          <Icon name="MessageSquare" size={48} className="mx-auto mb-6 text-secondary" />
          <h3 className="text-2xl font-bold mb-4">Don't See Your Industry?</h3>
          <p className="text-white/90 mb-6 max-w-2xl mx-auto">
            We're constantly expanding our industry-specific content. Let us know what resources 
            would be most valuable for your sector.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              variant="secondary"
              iconName="MessageCircle"
              iconPosition="left"
              className="hover:scale-105 active:scale-95 transition-all duration-200"
            >
              Request Industry Content
            </Button>
            <Button
              variant="outline"
              iconName="Calendar"
              iconPosition="left"
              className="hover:scale-105 active:scale-95 transition-all duration-200"
            >
              Schedule Consultation
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default IndustryResources;